import React, { useState, useEffect, useCallback, useRef, createRef, useMemo } from 'react';
import {
   Modal,
   ModalBody,
   ModalOverlay,
   ModalHeader,
   ModalContent,
   ModalCloseButton,
   ModalFooter,
   Button,
   useColorModeValue,
   Box,
   Grid,
   GridItem,
   Flex,
   Text,
   Divider,
   IconButton,
   VStack,
   HStack,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import InputField from 'components/ui/InputField';

const TakaForm = ({ isOpen, onClose, handleTakaDetails, currentTakaDetails, onCloseComplete }) => {
   const [rows, setRows] = useState([]);
   const qtyRef = useRef([]);
   const submitRef = useRef(null);
   const takaDetails = useMemo(() => {
      return currentTakaDetails
         ?.filter((item) => item.id || (item.quantity3 && Number(item.quantity3) > 0))
         ?.map((item) => ({ ...(item.id && { id: item.id }), qty: item.quantity3, minQty3: item.minQty3 }));
   }, [currentTakaDetails]);

   const totalTaka = useMemo(() => {
      return rows.reduce((acc, item) => acc + Number(item?.qty || 0), 0);
   }, [rows]);

   const prepareRowRefs = (count) => {
      qtyRef.current = Array(count)
         ?.fill()
         ?.map((_, i) => qtyRef.current[i] || createRef());
   };

   const handleAddRow = useCallback(() => {
      setRows([...rows, { qty: '' }]);
   }, [rows.length]);

   const handleRemoveRow = useCallback(
      (index) => {
         setRows(rows.filter((_, i) => i !== index));
      },
      [rows],
   );

   const handleOnClose = useCallback(() => {
      setRows([]);
      onClose();
   }, [onClose]);

   const handleKeyDown = useCallback(
      (e, nextRef, idx) => {
         const key = e.key;
         const isShift = e.shiftKey;
         const name = e.target.name;
         const value = e.target.value.trim();

         // Shift + Tab: Move backward
         if (key === 'Tab' && isShift) {
            e.preventDefault(); // Prevent default tabbing
            nextRef?.current[idx - 1]?.focus();
            return;
         }

         // Enter or Tab (no shift): Move forward or add row
         if (key === 'Enter' || (key === 'Tab' && !isShift)) {
            e.preventDefault(); // Prevent default behavior

            if (value !== '' && name === 'qty') {
               handleAddRow();
               nextRef?.current[idx + 1]?.focus();
            } else {
               handleRemoveRow(idx);
               submitRef.current?.focus(); // Go to submit button or similar
            }
         }
      },
      [handleAddRow, handleRemoveRow],
   );

   const handleOnChange = (e, idx) => {
      const updatedRows = [...rows];
      updatedRows[idx].qty = e.target.value;
      setRows(updatedRows);
   };

   const handleOnSubmit = () => {
      const takaDetails = rows;
      handleTakaDetails(takaDetails);
      handleOnClose();
   };

   useEffect(() => {
      prepareRowRefs(rows?.length);
   }, [rows?.length]);


   useEffect(() => {
      if (isOpen) {
         if (takaDetails?.length > 0) {
            setRows(takaDetails);
         } else if (rows.length === 0) {
            setRows([{ qty: '' }]);
         }
      }
   }, [takaDetails, isOpen]);

   const bgColor = useColorModeValue('white', 'gray.800');
   const headerBg = useColorModeValue('blue.50', 'blue.900');
   const totalBg = useColorModeValue('green.50', 'green.900');
   const totalColor = useColorModeValue('green.700', 'green.200');
   const cardBg = useColorModeValue('gray.50', 'gray.700');
   const borderColor = useColorModeValue('gray.200', 'gray.600');
   const hoverBorderColor = useColorModeValue('blue.300', 'blue.500');

   return (
      <Modal isOpen={isOpen} onClose={handleOnClose} isCentered onCloseComplete={onCloseComplete} size="xl">
         <ModalOverlay backdropFilter="blur(4px)" />
         <ModalContent
            bg={bgColor}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="16px"
            boxShadow="2xl"
            maxW="600px">
            <ModalHeader bg={headerBg} borderTopRadius="16px" py={4}>
               <VStack spacing={2} align="stretch">
                  <Flex justifyContent="space-between" alignItems="center">
                     <Text fontSize="xl" fontWeight="bold">
                        Add Taka Details
                     </Text>
                     <Box bg={totalBg} px={4} py={2} mr={4} borderRadius="md" border="2px solid" borderColor={totalColor}>
                        <Text fontSize="sm" color={totalColor} fontWeight="semibold">
                           Total: {totalTaka} MTS
                        </Text>
                     </Box>
                  </Flex>
                  <Text fontSize="sm" color="gray.500">
                     Entries: {rows?.length}
                  </Text>
               </VStack>
            </ModalHeader>
            <ModalCloseButton top={1} right={2} />

            <Divider />

            <ModalBody py={6} px={6}>
               <VStack
                  spacing={3}
                  width="100%"
                  maxH="450px"
                  overflowY="auto"
                  pr={2}
                  sx={{
                     '&::-webkit-scrollbar': {
                        width: '8px',
                     },
                     '&::-webkit-scrollbar-track': {
                        background: useColorModeValue('#f1f1f1', '#2d3748'),
                        borderRadius: '10px',
                     },
                     '&::-webkit-scrollbar-thumb': {
                        background: useColorModeValue('#888', '#4a5568'),
                        borderRadius: '10px',
                     },
                     '&::-webkit-scrollbar-thumb:hover': {
                        background: useColorModeValue('#555', '#718096'),
                     },
                  }}>
                  {rows.map((row, idx) => (
                     <Box
                        key={idx}
                        width="100%"
                        p={4}
                        bg={cardBg}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor={borderColor}
                        transition="all 0.2s"
                        _hover={{
                           boxShadow: 'md',
                           borderColor: hoverBorderColor,
                        }}>
                        <HStack spacing={4} align="flex-start">
                           <Box flex={1}>
                              <InputField
                                 ref={(el) => {
                                    if (qtyRef && qtyRef.current) qtyRef.current[idx] = el;
                                 }}
                                 type="number"
                                 id={`qty-${idx}`}
                                 name="qty"
                                 label={`Entry ${idx + 1} - MTS`}
                                 value={row.qty}
                                 onChange={(e) => handleOnChange(e, idx)}
                                 onKeyDown={(e) => handleKeyDown(e, qtyRef, idx)}
                                 autoFocus={idx === rows?.length - 1}
                                 onWheel={(e) => e.target.blur()}
                                 isDisabled={row.minQty3 > 0}
                                 opacity={row.minQty3 > 0 ? 0.7 : 1}
                                 style={{
                                    backgroundColor: row.minQty3 > 0 ? '#f5f5f5' : '',
                                    cursor: row.minQty3 > 0 ? 'not-allowed' : 'text',
                                 }}
                              />
                           </Box>
                           <HStack spacing={2}>
                              {rows?.length > 1 && (
                                 <IconButton
                                    icon={<MinusIcon />}
                                    colorScheme="red"
                                    size="md"
                                    onClick={() => handleRemoveRow(idx)}
                                    aria-label="Remove row"
                                    borderRadius="md"
                                    _focus={{ outlineColor: 'blue.500' }}
                                    isDisabled={row.minQty3 > 0}
                                 />
                              )}
                              {idx === rows?.length - 1 && (
                                 <IconButton
                                    icon={<AddIcon />}
                                    colorScheme="green"
                                    size="md"
                                    onClick={handleAddRow}
                                    aria-label="Add row"
                                    borderRadius="md"
                                    _focus={{ outlineColor: 'blue.500' }}
                                 />
                              )}
                           </HStack>
                        </HStack>
                     </Box>
                  ))}
               </VStack>
            </ModalBody>

            <Divider />

            <ModalFooter py={4} px={6}>
               <HStack spacing={3} width="100%">
                  <Button
                     colorScheme="red"
                     onClick={() => handleOnClose()}
                     flex={1}
                     variant="outline"
                     size="lg"
                     borderRadius="md"
                     fontWeight="semibold">
                     Cancel
                  </Button>
                  <Button
                     ref={submitRef}
                     colorScheme="blue"
                     flex={1}
                     onClick={() => handleOnSubmit()}
                     _focus={{ outlineColor: 'blue.500' }}
                     size="lg"
                     borderRadius="md"
                     fontWeight="semibold">
                     Save Changes
                  </Button>
               </HStack>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default TakaForm;
